import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, StatusBar, ActivityIndicator, ScrollView, TouchableOpacity, Image } from 'react-native';
import type { V2Balance } from '../../types/v2';
import TabBar from '../../components/TabBar';
import SimpleHeader from '../../components/SimpleHeader';
import { COLORS } from '../../theme/themeTokens';
import { v2GetCard, v2GetCardTransactions } from '../../services/v2/api';
import CardHistoryPeriodScreen, { PeriodResult } from './CardHistoryPeriodScreen';
import { maskedCardNumber } from '../../utils/cardNumber';
import { useLatestRequest } from '../../hooks/useLatestRequest';

// ──────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────

interface CardHistoryScreenProps {
  balance: V2Balance;
  onBack: () => void;
  onNavigate: (screen: string) => void;
  onTransactionPress: (tx: any) => void;
}

// ──────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────

const CardHistoryScreen: React.FC<CardHistoryScreenProps> = ({ balance, onBack, onNavigate, onTransactionPress }) => {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'deposit' | 'withdraw'>('all');
  const [periodModalVisible, setPeriodModalVisible] = useState(false);
  const [periodDays, setPeriodDays] = useState<number>(30); // default 30 days
  // The card these rows belong to, as "**** 9929". /card/:id carries the
  // masked number as public metadata — the full PAN lives behind the
  // separate sensitive endpoint and is never wanted here.
  const [cardNumber, setCardNumber] = useState<string | null>(null);

  // The period can be switched while the last one is still loading; only
  // the answer to the newest request is shown.
  const latest = useLatestRequest();

  const fetchTransactions = async (daysBack: number) => {
    const cardId = balance.wallet_address ?? '';
    if (!cardId) { return; }
    const ticket = latest.begin();
    try {
      setTxLoading(true);
      const pad = (n: number) => String(n).padStart(2, '0');
      const fmt = (d: Date) =>
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      const now = new Date();
      const fromDate = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);
      console.log('[CardHistoryScreen] REQUEST /api/v2/user/cards/:id/transactions', { cardId, from: fmt(fromDate), to: fmt(now) });
      const res = await v2GetCardTransactions(cardId, fmt(fromDate), fmt(now));
      console.log('[CardHistoryScreen] RESPONSE /api/v2/user/cards/:id/transactions', JSON.stringify(res, null, 2));
      const txList = Array.isArray(res?.transactions) ? res.transactions : [];
      if (latest.isLatest(ticket)) setTransactions(txList);
    } catch (err) {
      console.warn('[CardHistoryScreen] v2GetCardTransactions failed for', cardId, err);
    } finally {
      if (latest.isLatest(ticket)) setTxLoading(false);
    }
  };

  const fetchTransactionsByRange = async (from: Date, to: Date) => {
    const cardId = balance.wallet_address ?? '';
    if (!cardId) { return; }
    const ticket = latest.begin();
    try {
      setTxLoading(true);
      const pad = (n: number) => String(n).padStart(2, '0');
      const fmt = (d: Date) =>
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      // from = start of day, to = end of day
      const fromStart = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0);
      const toEnd = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59);
      console.log('[CardHistoryScreen] REQUEST (range) /api/v2/user/cards/:id/transactions', { cardId, from: fmt(fromStart), to: fmt(toEnd) });
      const res = await v2GetCardTransactions(cardId, fmt(fromStart), fmt(toEnd));
      console.log('[CardHistoryScreen] RESPONSE (range) /api/v2/user/cards/:id/transactions', JSON.stringify(res, null, 2));
      const txList = Array.isArray(res?.transactions) ? res.transactions : [];
      if (latest.isLatest(ticket)) setTransactions(txList);
    } catch (err) {
      console.warn('[CardHistoryScreen] v2GetCardTransactions (range) failed for', cardId, err);
    } finally {
      if (latest.isLatest(ticket)) setTxLoading(false);
    }
  };

  useEffect(() => {
    // initial load: from 12.01.2026 00:00:00 to today 23:59:59
    const defaultFrom = new Date(2026, 0, 12, 0, 0, 0); // 12 Jan 2026
    const now = new Date();
    const defaultTo = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    fetchTransactionsByRange(defaultFrom, defaultTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balance.wallet_address]);

  // The number comes from the card, not from the transactions: those are
  // passed through from Payoma untyped, and nothing in the app reads a
  // number out of them. One fetch — every row on this screen belongs to
  // the same card.
  useEffect(() => {
    let mounted = true;
    const cardId = balance.wallet_address ?? '';
    if (!cardId) { return; }
    v2GetCard(cardId)
      .then((res) => {
        // Payoma's whole masked number — "5303 26** **** 9929" — did not
        // fit the row and pushed the rest of the line out of sight. The
        // last four are the part that says which card this was.
        if (mounted) { setCardNumber(maskedCardNumber(res?.data?.number)); }
      })
      .catch((err) => console.warn('[CardHistoryScreen] v2GetCard failed for', cardId, err));
    return () => { mounted = false; };
  }, [balance.wallet_address]);

  /** Format number with space as thousands separator and dot as decimal separator. */
  const formatAmount = (value: number | string | null): string => {
    if (value == null) return '';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return String(value);
    const [intPart, decPart] = num.toFixed(2).split('.');
    const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${withSpaces}.${decPart}`;
  };

  /** Format "YYYY-MM-DD" → "dd.MM.yyyy" */
  const formatDate = (iso: string): string => {
    const parts = iso.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return iso;
  };

  /** Extract the raw numeric amount from a transaction */
  const getTxRawAmount = (tx: any): number => {
    if (typeof tx.amount === 'object' && tx.amount !== null) {
      return Number(tx.amount.amount ?? 0);
    }
    return Number(tx.amount ?? 0);
  };

  // Group transactions by date (YYYY-MM-DD part of created_at), applying current filter
  const groupedTransactions = React.useMemo(() => {
    const filtered = filter === 'all'
      ? [...transactions]
      : transactions.filter((tx) => {
          const raw = getTxRawAmount(tx);
          return filter === 'deposit' ? raw > 0 : raw < 0;
        });

    // Sort by created_at descending (newest first)
    filtered.sort((a, b) => {
      const dateA = a.created_at ?? '';
      const dateB = b.created_at ?? '';
      return dateB.localeCompare(dateA);
    });

    const groups: { date: string; items: any[] }[] = [];
    const map = new Map<string, any[]>();
    for (const tx of filtered) {
      const createdAt: string = tx.created_at ?? '';
      const dateKey = createdAt.split(' ')[0] || createdAt;
      if (!map.has(dateKey)) {
        const items: any[] = [];
        map.set(dateKey, items);
        groups.push({ date: formatDate(dateKey), items });
      }
      map.get(dateKey)!.push(tx);
    }
    return groups;
  }, [transactions, filter]);

  // Display groups (no pagination) and compute total count
  const { pagedGroups, totalFiltered } = useMemo(() => {
    const groups = groupedTransactions;
    const total = groups.reduce((sum, g) => sum + (g.items?.length || 0), 0);
    return { pagedGroups: groups, totalFiltered: total };
  }, [groupedTransactions]);

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />

      <SimpleHeader
        title="History"
        onBack={onBack}
        backgroundVariant="primaryNavy"
      />

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Filter buttons */}
        <View style={styles.filterRow}>
          {(['all', 'deposit', 'withdraw'] as const).map((key) => {
            const labels = { all: 'All', deposit: 'Deposit', withdraw: 'Withdraw' };
            const isActive = filter === key;
            return (
              <TouchableOpacity
                key={key}
                style={[
                  styles.filterBtn,
                  { backgroundColor: isActive ? COLORS.steelNavy : COLORS.white },
                ]}
                onPress={() => { setFilter(key); }}
                activeOpacity={0.7}
              >
                <Text style={[styles.filterBtnText, isActive && styles.filterBtnTextActive]}>
                  {labels[key]}
                </Text>
              </TouchableOpacity>
            );
          })}
          <TouchableOpacity
            style={[
              styles.filterBtn,
              { marginLeft: 'auto', backgroundColor: COLORS.steelNavy },
            ]}
            onPress={() => setPeriodModalVisible(true)}
            activeOpacity={0.7}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Text style={[styles.filterBtnText, { color: COLORS.white }]}>Period</Text>
              <Image
                source={require('../../assets/arrow_left_20_white.png')}
                style={{ width: 16, height: 16, marginLeft: 6, tintColor: COLORS.white, transform: [{ rotate: '-90deg' }] }}
                resizeMode="contain"
              />
            </View>
          </TouchableOpacity>
        </View>

        {txLoading ? (
          <ActivityIndicator size="large" color={COLORS.brandOrange} style={styles.loader} />
        ) : pagedGroups.length === 0 ? (
          <Text style={styles.historyEmpty}>No transactions</Text>
        ) : (
          pagedGroups.map((group) => (
            <View key={group.date} style={styles.historyGroup}>
              <Text style={styles.historyDate}>{group.date}</Text>
              {group.items.map((tx: any, idx: number) => (
                <TouchableOpacity
                  key={tx.id ?? idx}
                  style={styles.historyRow}
                  onPress={() => onTransactionPress(tx)}
                  activeOpacity={0.7}
                >
                  <Image
                    source={require('../../assets/creditcard_white.png')}
                    style={styles.historyRowIcon}
                  />
                  <View style={styles.historyLeft}>
                    <Text style={styles.historyName} numberOfLines={1}>
                      {tx.terminal?.name ?? 'Deposit'}
                    </Text>
                    {/* The card this went through, and nothing else. The
                        kind of operation — "Card (ECOMM)", "POS wireless"
                        — used to share the line and then to crowd it out;
                        it says little a client acts on, and it is still in
                        the data if the transaction screen ever wants it.
                        No line at all when the card's number never
                        arrived: an empty grey line reads as a fault. */}
                    {!!cardNumber && (
                      <Text style={styles.historyType} numberOfLines={1}>
                        {cardNumber}
                      </Text>
                    )}
                  </View>
                  <Text style={[styles.historyAmount, getTxRawAmount(tx) > 0 && styles.historyAmountPositive]}>
                    {(() => {
                      const raw = getTxRawAmount(tx);
                      const prefix = raw > 0 ? '+' : '';
                      const formatted = typeof tx.amount === 'object' && tx.amount !== null
                        ? `${formatAmount((tx.amount.amount ?? 0) / 100)} ${tx.amount.currency ?? ''}`
                        : `${formatAmount((tx.amount ?? 0) / 100)} ${tx.currency ?? ''}`;
                      return `${prefix}${formatted}`;
                    })()}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          ))
        )}

  {/* Pagination removed - show full history */}
      </ScrollView>

      <CardHistoryPeriodScreen
        visible={periodModalVisible}
        onClose={(result: PeriodResult) => {
          setPeriodModalVisible(false);
          if (!result) return;
          if ('days' in result) {
            setPeriodDays(result.days);
            fetchTransactions(result.days);
          } else if ('from' in result && 'to' in result) {
            fetchTransactionsByRange(result.from, result.to);
          }
        }}
      />

      <TabBar activeTab="dashboard" onNavigate={onNavigate} />
    </View>
  );
};

// ──────────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  content: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 120,
  },
  loader: {
    marginVertical: 40,
  },
  historyEmpty: {
    fontSize: 16,
    color: COLORS.blueSteel,
    textAlign: 'center',
    paddingVertical: 12,
  },
  historyGroup: {
    marginBottom: 8,
  },
  historyDate: {
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '400',
    color: COLORS.blueSteel,
    marginBottom: 6,
  },
  historyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  historyRowIcon: {
    width: 28,
    height: 28,
    marginRight: 10,
    tintColor: COLORS.steelNavy,
  },
  historyLeft: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  historyName: {
    flex: 1,
    fontSize: 18,
    lineHeight: 22,
    color: COLORS.primaryNavy,
    fontWeight: '400',
  },
  historyType: {
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '400',
    color: COLORS.blueSteel,
  },
  historyAmount: {
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '500',
    color: COLORS.primaryNavy,
  },
  historyAmountPositive: {
    color: COLORS.mint,
  },
  filterRow: {
    flexDirection: 'row',
    marginBottom: 16,
    gap: 8,
  },
  filterBtn: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: COLORS.white,
    alignItems: 'center',
  },
  filterBtnActive: {
    backgroundColor: COLORS.steelNavy,
  },
  filterBtnText: {
    fontFamily: 'Inter',
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '500',
    color: COLORS.primaryNavy,
  },
  filterBtnTextActive: {
    color: COLORS.white,
  },
  paginationRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 8,
    gap: 20,
  },
  paginationBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: COLORS.steelNavy,
    justifyContent: 'center',
    alignItems: 'center',
  },
  paginationBtnDisabled: {
    backgroundColor: '#D1D5DB',
  },
  paginationArrow: {
    width: 20,
    height: 20,
    tintColor: COLORS.white,
  },
  paginationArrowRight: {
    transform: [{ rotate: '180deg' }],
  },
  paginationArrowDisabled: {
    tintColor: '#9CA3AF',
  },
  paginationText: {
    fontSize: 17,
    fontWeight: '600',
    color: COLORS.primaryNavy,
  },
});

export default CardHistoryScreen;
